import { useMemo, useRef } from "react";
import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { ProductionHistory, ProductionHistoryWindow } from "@/features/devices/types";
import { projectChartPoints, resolveChartConfig, summarizeRateWindow } from "../../chartWidgets";
import { aggregateSourceState, describePartialState } from "../../multiSource";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import type { WidgetHistoryState } from "../../useProductionHistories";
import { describeSignalDiagnostic } from "./diagnosticMeaning";
import { formatWidgetNumber } from "./format";
import { HeroCard, HeroMeaning, HeroWindowPicker } from "./HeroCard";
import { TimeSeriesChart, type TimeSeriesExtraSeries, type TimeSeriesPoint } from "./TimeSeriesChart";
import { describeQuality } from "./quality";

interface MultiSeriesChartWidgetProps {
  widget: DashboardWidget;
  values: ResolvedWidgetValue[];
  /** Estado do histórico POR FONTE, na ordem das fontes. */
  histories: Array<WidgetHistoryState | undefined>;
  labels?: Record<string, string>;
  editable?: boolean;
  onConfigure?: () => void;
  /** Desfaz o "Gerar grafico": so existe em card que nasceu de um. */
  onHideChart?: () => void;
  onChangeWindow?: (window: ProductionHistoryWindow) => void;
}

/**
 * Gráfico multissérie: N séries no MESMO eixo, para comparar fases, pontos ou
 * zonas ao longo do tempo.
 *
 * A comparação é o produto. Três gráficos empilhados com escalas automáticas
 * independentes fazem três curvas idênticas parecerem diferentes (e vice-versa)
 * — por isso o domínio do eixo Y cobre TODAS as séries, e por isso o editor só
 * deixa combinar fontes de mesma unidade e mesmo `kind`.
 *
 * A quebra de linha em falha vale por série: uma fase em timeout interrompe só
 * a curva dela, e essa assimetria é justamente o que o operador precisa ver.
 */
export function MultiSeriesChartWidget({
  widget,
  values,
  histories,
  labels,
  editable,
  onConfigure,
  onHideChart,
  onChangeWindow,
}: MultiSeriesChartWidgetProps) {
  const config = resolveChartConfig(widget);
  const unit = widget.settings.unit?.trim() || null;
  const decimals = widget.settings.decimals ?? 1;

  const anchorHistory = histories[0];
  const busy = histories.some((history) => history?.status === "reloading");
  const anyLoading = histories.some(
    (history) => !history || history.status === "loading",
  );
  const unavailable = histories.some((history) => history?.status === "unavailable");
  const errored = histories.some((history) => history?.status === "error");

  // A janela do EIXO segue o dado já carregado, não a recém-pedida: durante a
  // troca de janela a curva visível ainda é a anterior.
  const dataWindow = (anchorHistory?.status === "ready" || anchorHistory?.status === "reloading"
    ? anchorHistory.data.window
    : config.window) as ProductionHistoryWindow;
  const resolution = anchorHistory?.status === "ready" || anchorHistory?.status === "reloading"
    ? anchorHistory.data.resolution : null;
  const aggregation = resolution && config.series === "rate"
    ? ` · médias por ${resolution === "1m" ? "minuto" : resolution === "1h" ? "hora" : "dia"}` : "";

  // O array externo muda com o tick ao vivo; os historicos nao.
  const inputs = histories.map((h): ProductionHistory | undefined =>
    h?.status === "ready" || h?.status === "reloading" ? h.data : undefined);
  const previous = useRef(inputs);
  if (inputs.length !== previous.current.length || inputs.some((input, i) => input !== previous.current[i])) previous.current = inputs;
  const historyData = previous.current;
  const projected = useMemo(() => historyData.map((history) => history
    ? projectChartPoints(history.points, config.series, dataWindow) : []), [historyData, config.series, dataWindow]);
  const seriesFor = (index: number): TimeSeriesPoint[] => projected[index] ?? [];
  // Media da janela POR FONTE, na legenda. Nao vai como linha no grafico: tres
  // tracejadas quase coincidentes so esconderiam as curvas que o card compara.
  const means = useMemo(() => historyData.map((history) =>
    history && config.series === "rate" ? summarizeRateWindow(history).mean : null), [historyData, config.series]);

  const sourceAt = (index: number): string => values[index]?.source ?? widget.source;
  const labelFor = (index: number): string => {
    const source = sourceAt(index);
    return labels?.[source] ?? formatPublicSourceLabel(source, widget.settings.signalKind);
  };

  const anchorPoints = seriesFor(0);
  // Séries de todas as fontes, INCLUSIVE as vazias. Filtrar as sem pontos aqui
  // faria a fonte sumir também da legenda — o operador veria duas curvas e
  // concluiria que o painel tem duas fases. Uma série vazia não desenha nada,
  // mas continua listada e marcada.
  const extraSeriesAll: TimeSeriesExtraSeries[] = values.slice(1).map((_value, offset) => ({
    points: seriesFor(offset + 1),
    label: labelFor(offset + 1),
    // A cor acompanha a POSICAO da fonte, nao a da linha desenhada. Assim uma
    // fase que fica sem serie e volta mantem a mesma cor, e o swatch da
    // legenda nunca aponta para a curva de outra fonte.
    colorIndex: offset + 1,
  }));
  const extraSeries: TimeSeriesExtraSeries[] = extraSeriesAll.filter(
    (serie) => serie.points.length > 0,
  );

  const hasPlottable =
    anchorPoints.some((point) => point.value !== null) ||
    extraSeries.some((serie) => serie.points.some((point) => point.value !== null));

  const state = aggregateSourceState(values);
  const diagnosed = describeSignalDiagnostic(state.diagnostic ?? undefined);
  const partial = describePartialState(state);
  const meaning = partial
    ? { tone: "warn" as const, text: partial }
    : state.availableCount === 0 && state.quality !== "live"
      ? { tone: state.quality === "stale" ? "warn" as const : "muted" as const,
          text: `${describeQuality(state.quality).title} · exibindo apenas histórico` }
      : diagnosed;
  const cardTone = diagnosed?.tone === "danger" ? "danger" : meaning ? "warn" : "ok";

  // A legenda espelha as FONTES, não as séries desenhadas: quem não tem
  // histórico aparece marcado em vez de desaparecer.
  // A legenda usa O MESMO `colorIndex` que a linha — e a unica forma de o
  // swatch nao mentir sobre qual curva pertence a qual fonte.
  //
  // A identidade de cada item e a FONTE, nao o rotulo. `shortLabel` descarta o
  // nome do valor ("Tensao L1 — Leitura" vira "Tensao L1"), entao dois pontos
  // que o operador batizou igual no configurador colidiriam: React reclamaria
  // de chaves duplicadas e poderia reaproveitar o no errado ao alternar series
  // vazias. A tag da fonte e unica por construcao.
  const legend = [
    {
      source: sourceAt(0),
      label: labelFor(0),
      index: 0,
      empty: !anchorPoints.some((point) => point.value !== null),
      mean: means[0] ?? null,
    },
    ...extraSeriesAll.map((serie, offset) => ({
      source: sourceAt(offset + 1),
      label: serie.label,
      index: serie.colorIndex,
      empty: !serie.points.some((point) => point.value !== null),
      mean: means[offset + 1] ?? null,
    })),
  ];

  return (
    <HeroCard
      title={widget.title}
      caption={unit ? `Comparativo no tempo · ${unit}` : "Comparativo no tempo"}
      tone={cardTone}
      editable={editable}
      onConfigure={onConfigure}
      onHideChart={onHideChart}
      footnote={null}
    >
      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      <div
        className={busy ? "w-hero__trend w-hero__trend--busy" : "w-hero__trend"}
        aria-busy={busy || undefined}
      >
        <div className="w-hero__trend-hd">
          <span>{legend.length} séries{aggregation}</span>
          {onChangeWindow ? (
            <HeroWindowPicker
              value={config.window}
              busy={busy}
              onChange={onChangeWindow}
              ariaLabel={`Período de ${widget.title}`}
            />
          ) : null}
        </div>

        {hasPlottable ? (
          <>
            <TimeSeriesChart
              points={anchorPoints}
              extraSeries={extraSeries}
              kind="line"
              window={dataWindow}
              unit={unit ?? undefined}
              decimals={decimals}
              yScale={config.yScale}
              min={widget.settings.min}
              max={widget.settings.max}
              thresholds={widget.settings.thresholds}
              title={widget.title}
              seriesLabel={labelFor(0)}
              valueLabel={config.series === "rate" ? "média" : undefined}
            />
          </>
        ) : (
          <p className="w-hero__trend-empty">
            {unavailable
              ? "Ative o Timescale para ver as séries."
              : errored
                ? "Não foi possível carregar as séries."
                : anyLoading
                  ? "Carregando séries…"
                  : "Ainda não há amostras nesta janela."}
          </p>
        )}
        <ul className="w-multi__legend">
          {legend.map((item) => (
            <li
              key={item.source}
              className={item.empty
                ? "w-multi__legend-item w-multi__legend-item--empty"
                : "w-multi__legend-item"}
            >
              <span
                className="w-multi__legend-swatch"
                data-series={item.index % 5}
                aria-hidden="true"
              />
              {item.label}
              {item.empty ? " · sem série" : null}
              {item.mean !== null ? (
                <span className="w-multi__legend-mean">
                  {` · média ${formatWidgetNumber(item.mean, decimals)}${unit ? ` ${unit}` : ""}`}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </HeroCard>
  );
}
